/**
 * A client's request: the estimates builders sent (compare up to three side
 * by side, shortlist, ask for a revision, decline, order), one message thread
 * per builder, and the brief they priced. Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, mktError, money } from '@/api/marketplace'
import {
  askRevision, closeRequest, compareQuotes, declineQuote, getRequest, requestMessages, sendRequestMessage, shortlistQuote,
  type BuildRequest, type Quote, type TimelineEvent,
} from '@/api/marketplaceBuild'
import { useAuth } from '@/auth/AuthContext'
import MarketplaceShell from '../MarketplaceShell'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import {
  Avatar, briefItems, covers, coveredCount, dateTime, ErrorPanel, firstYear, Loading, monthlyShort, monthlyText,
  monthlyTotal, quoteLive, quoteStatus, requestStatus, weeksText,
} from './clientShared'

type Tab = 'est' | 'short' | 'msg' | 'brief'
type Sort = 'match' | 'year' | 'price' | 'fast'
const MAX_COMPARE = 3

export default function RequestPage() {
  const { id = '' } = useParams()
  const reqId = Number(id)
  const { user } = useAuth()

  const [req, setReq] = useState<BuildRequest | null>(null)
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [quotesError, setQuotesError] = useState<string | null>(null)

  const [tab, setTab] = useState<Tab>('est')
  const [sort, setSort] = useState<Sort>('match')
  const [selId, setSelId] = useState<number | null>(null)
  const [cmp, setCmp] = useState<number[]>([])
  const [confirmClose, setConfirmClose] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [revising, setRevising] = useState(false)
  const [revisionText, setRevisionText] = useState('')
  const [confirmDecline, setConfirmDecline] = useState(false)

  const [thread, setThread] = useState<string | null>(null)
  const [messages, setMessages] = useState<TimelineEvent[] | null>(null)
  const [msgError, setMsgError] = useState<string | null>(null)
  const [msgText, setMsgText] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null); setQuotesError(null)
    try {
      const r = await getRequest(reqId)
      setReq(r)
      if (r.status !== 'draft') {
        try {
          const c = await compareQuotes(reqId)
          setQuotes(c.quotes)
          setSelId((cur) => cur ?? c.quotes[0]?.id ?? null)
        } catch (e) {
          setQuotesError(mktError(e, 'The estimates could not be loaded.').message)
        }
      }
    } catch (e) {
      setError(mktError(e, 'This request could not be loaded.').message)
    } finally {
      setLoading(false)
    }
  }, [reqId])
  useEffect(() => { load() }, [load])

  const items = useMemo(() => briefItems(req), [req])
  const mergeQuote = (q: Quote) => setQuotes((list) => list.map((x) => (x.id === q.id ? { ...x, ...q } : x)))

  // Messages: one thread per builder who sent an estimate.
  const builders = useMemo(() => {
    const seen = new Set<string>()
    return quotes.filter((q) => q.builder.user_id && !seen.has(q.builder.user_id) && seen.add(q.builder.user_id)).map((q) => q.builder)
  }, [quotes])

  const loadThread = useCallback(async (builderUserId: string) => {
    setMessages(null); setMsgError(null)
    try {
      const r = await requestMessages(reqId, builderUserId)
      setMessages(r.messages)
    } catch (e) {
      setMsgError(mktError(e, 'The messages could not be loaded.').message)
    }
  }, [reqId])
  useEffect(() => { if (tab === 'msg' && thread) loadThread(thread) }, [tab, thread, loadThread])
  useEffect(() => { if (tab === 'msg' && !thread && builders[0]?.user_id) setThread(builders[0].user_id) }, [tab, thread, builders])

  const send = async () => {
    const text = msgText.trim()
    if (!text || !thread) return
    setSending(true)
    try {
      const m = await sendRequestMessage(reqId, text, thread)
      setMessages((list) => [...(list || []), m]); setMsgText('')
    } catch (e) {
      toast.error(mktError(e, 'The message could not be sent.').message)
    } finally {
      setSending(false)
    }
  }

  const doClose = async () => {
    setBusy('close')
    try {
      const r = await closeRequest(reqId)
      setReq((cur) => ({ ...(cur as BuildRequest), ...r })); setConfirmClose(false)
      toast.success('Request closed. Builders can no longer send estimates.')
    } catch (e) {
      toast.error(mktError(e, 'The request could not be closed.').message)
    } finally {
      setBusy(null)
    }
  }

  const toggleShort = async (q: Quote) => {
    setBusy(`short:${q.id}`)
    try { mergeQuote(await shortlistQuote(q.id, !q.shortlisted)) } catch (e) { toast.error(mktError(e).message) } finally { setBusy(null) }
  }

  const sendRevision = async (q: Quote) => {
    const note = revisionText.trim()
    if (!note) { toast.error('Say what should change.'); return }
    setBusy('revision')
    try {
      mergeQuote(await askRevision(q.id, note))
      setRevising(false); setRevisionText('')
      toast.success(`Revision asked. ${q.builder.name} will send a new version.`)
    } catch (e) {
      toast.error(mktError(e, 'The revision could not be asked.').message)
    } finally {
      setBusy(null)
    }
  }

  const doDecline = async (q: Quote) => {
    setBusy('decline')
    try {
      mergeQuote(await declineQuote(q.id))
      setConfirmDecline(false)
      toast.success(`${q.builder.name} has been told.`)
    } catch (e) {
      toast.error(mktError(e, 'The estimate could not be declined.').message)
    } finally {
      setBusy(null)
    }
  }

  const select = (qid: number) => { setSelId(qid); setRevising(false); setConfirmDecline(false) }
  const toggleCmp = (qid: number) => setCmp((c) => (c.includes(qid) ? c.filter((x) => x !== qid) : c.length >= MAX_COMPARE ? c : [...c, qid]))

  if (loading) return <MarketplaceShell><Loading text="Loading your request…" /></MarketplaceShell>
  if (error || !req) return <MarketplaceShell><ErrorPanel message={error || 'This request could not be loaded.'} onRetry={load} /></MarketplaceShell>

  const cur = req.currency
  const st = requestStatus(req)
  const live = quotes.filter((q) => q.status !== 'withdrawn' && q.status !== 'draft')
  const prices = live.map((q) => q.price_minor).filter((p) => p > 0).sort((a, b) => a - b)
  const weeks = live.map((q) => q.weeks || 0).filter((w) => w > 0).sort((a, b) => a - b)
  const median = (a: number[]) => (a.length ? a[Math.floor((a.length - 1) / 2)] : 0)
  const mostWeeks = (() => {
    const n: Record<number, number> = {}
    weeks.forEach((w) => { n[w] = (n[w] || 0) + 1 })
    const best = Object.entries(n).sort((a, b) => b[1] - a[1])[0]
    return best ? Number(best[0]) : 0
  })()
  const covOf = (q: Quote) => q.covered ?? coveredCount(q, items)
  const totalOf = (q: Quote) => q.brief_total ?? items.length

  const sorters: Record<Sort, (a: Quote, b: Quote) => number> = {
    match: (a, b) => (covOf(b) / Math.max(1, totalOf(b))) - (covOf(a) / Math.max(1, totalOf(a))) || a.price_minor - b.price_minor,
    year: (a, b) => (firstYear(a) ?? Number.MAX_SAFE_INTEGER) - (firstYear(b) ?? Number.MAX_SAFE_INTEGER) || a.price_minor - b.price_minor,
    price: (a, b) => a.price_minor - b.price_minor,
    fast: (a, b) => (a.weeks || 999) - (b.weeks || 999),
  }
  const sorted = quotes.slice().sort(sorters[sort])
  const shown = tab === 'short' ? sorted.filter((q) => q.shortlisted) : sorted
  const sel = quotes.find((q) => q.id === selId) || shown[0] || null
  const cmpList = quotes.filter((q) => cmp.includes(q.id))
  const shortCount = quotes.filter((q) => q.shortlisted).length

  const tabs: [Tab, string][] = [
    ['est', `Estimates (${quotes.length})`], ['short', `Shortlisted (${shortCount})`], ['msg', 'Messages'], ['brief', 'Your brief'],
  ]

  return (
    <MarketplaceShell>
      <div className="mkt-wrap" style={{ paddingTop: 24, paddingBottom: 64, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Link to="/marketplace/build" style={{ fontSize: 14, textDecoration: 'none' }}>← Get it built</Link>

        <div className="mkt-panel" style={{ display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 520px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div className="mkt-row" style={{ gap: 10 }}>
              <h1 style={{ fontSize: 28 }}>{req.title}</h1>
              <span className={`mkt-pill ${st.tone}`}>{st.label}</span>
              <span className="mkt-pill">{req.visibility === 'invite' ? 'Invite only' : 'All Twasta builders'}</span>
            </div>
            {req.description && <p className="mkt-muted" style={{ margin: 0, whiteSpace: 'pre-line' }}>{req.description}</p>}
            <div className="mkt-row" style={{ gap: 6 }}>
              <span className="mkt-pill info">{req.kind_label}</span>
              {req.brief.map((s) => <span key={s.h} className="mkt-pill info">{s.h} ({s.items.length})</span>)}
              {req.feature_labels.map((f) => <span key={f} className="mkt-pill">{f}</span>)}
            </div>
          </div>
          <div className="mkt-col" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="mkt-row" style={{ gap: 8 }}>
              {req.status === 'draft' && <Link className="btn btn-primary btn-sm" to={`/marketplace/build/new/${req.id}`}>Continue</Link>}
              {req.status === 'open' && !confirmClose && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmClose(true)}>Close request</button>}
            </div>
            {confirmClose && (
              <div className="mkt-banner warn" role="alert" style={{ maxWidth: 420 }}>
                <div><strong>Close this request?</strong>Builders can no longer send estimates. Estimates you already have stay here.</div>
                <div className="mkt-row" style={{ gap: 8 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={doClose} disabled={busy === 'close'}>{busy === 'close' ? 'Closing…' : 'Yes, close it'}</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmClose(false)}>Keep it open</button>
                </div>
              </div>
            )}
          </div>
          {req.status !== 'draft' && (
            <div style={{ flex: '1 1 100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, borderTop: '1px solid var(--color-border)', paddingTop: 16 }}>
              <div><div className="mkt-faint">Estimates</div><div style={{ fontSize: 22, fontWeight: 700 }}>{live.length}</div><div className="mkt-faint">{shortCount ? `${shortCount} shortlisted` : 'None shortlisted yet'}</div></div>
              <div><div className="mkt-faint">Price range</div><div style={{ fontSize: 22, fontWeight: 700 }}>{prices.length ? (prices.length > 1 ? `${money(prices[0], cur)} – ${money(prices[prices.length - 1], cur)}` : money(prices[0], cur)) : '—'}</div><div className="mkt-faint">{prices.length > 2 ? `Middle: ${money(median(prices), cur)}` : ' '}</div></div>
              <div><div className="mkt-faint">Time range</div><div style={{ fontSize: 22, fontWeight: 700 }}>{weeks.length ? (weeks[0] === weeks[weeks.length - 1] ? weeksText(weeks[0]) : `${weeks[0]} – ${weeks[weeks.length - 1]} weeks`) : '—'}</div><div className="mkt-faint">{weeks.length > 2 ? `Most say ${mostWeeks}` : ' '}</div></div>
              <div><div className="mkt-faint">{req.status === 'open' ? 'Open for estimates until' : 'Posted'}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{fmtDate(req.status === 'open' ? req.closes_at : req.posted_at) || '—'}</div><div className="mkt-faint">{req.posted_at && req.status === 'open' ? `Posted ${fmtDate(req.posted_at)}` : ' '}</div></div>
            </div>
          )}
        </div>

        {req.status === 'draft' ? (
          <div className="mkt-empty">
            <strong>This request hasn’t been posted yet</strong>
            <span className="mkt-muted">Finish it and post it — builders can only send estimates once it is posted.</span>
            {req.problems?.length > 0 && <span className="mkt-faint">Still needed: {req.problems.join(' ')}</span>}
            <Link className="btn btn-primary" to={`/marketplace/build/new/${req.id}`}>Continue</Link>
          </div>
        ) : (
          <>
            <div role="tablist" aria-label="Request sections" className="mkt-tabs">
              {tabs.map(([t, label]) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} className={`mkt-tab${tab === t ? ' on' : ''}`} onClick={() => setTab(t)}>{label}</button>
              ))}
            </div>

            {(tab === 'est' || tab === 'short') && (
              <div className="mkt-col" style={{ gap: 16 }}>
                {quotesError && <div className="mkt-banner bad" role="alert"><div><strong>The estimates didn’t load</strong>{quotesError}</div><button type="button" className="btn btn-secondary btn-sm" onClick={load}>Try again</button></div>}

                {quotes.length > 1 && (
                  <div className="mkt-row" role="group" aria-label="Sort estimates" style={{ gap: 8 }}>
                    <span className="mkt-faint">Sort</span>
                    {([['match', 'Best match'], ['year', 'Lowest first-year cost'], ['price', 'Lowest price to build'], ['fast', 'Fastest']] as [Sort, string][]).map(([k, label]) => (
                      <button key={k} type="button" className={`mkt-chip${sort === k ? ' on' : ''}`} aria-pressed={sort === k} onClick={() => setSort(k)}>{label}</button>
                    ))}
                  </div>
                )}

                {cmpList.length >= 2 && <CompareTable list={cmpList} items={items} cur={cur} onClear={() => setCmp([])} />}

                {shown.length === 0 ? (
                  <div className="mkt-empty">
                    {tab === 'short'
                      ? <><strong>Nothing shortlisted yet</strong><span className="mkt-muted">Shortlist the estimates you like to keep them together here.</span></>
                      : <><strong>No estimates yet</strong><span className="mkt-muted">{req.status === 'open' ? 'Builders who match your request can send estimates until it closes. Each one appears here as it arrives.' : 'No builder sent an estimate for this request.'}</span></>}
                  </div>
                ) : (
                  <div className="mkt-split" style={{ gap: 18 }}>
                    <div style={{ flex: '1 1 440px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {cmp.length > 0 && cmpList.length < 2 && <span className="mkt-faint">Tick one more estimate to compare them side by side.</span>}
                      {shown.map((q) => {
                        const selected = sel?.id === q.id
                        const qs = quoteStatus(q)
                        const cov = covOf(q), total = totalOf(q)
                        const inCmp = cmp.includes(q.id)
                        return (
                          <div key={q.id} className="mkt-panel" style={{ padding: 0, display: 'flex', flexDirection: 'column', border: selected ? '2px solid var(--color-accent)' : undefined, boxShadow: selected ? '0 8px 24px color-mix(in srgb, var(--color-accent) 12%, transparent)' : undefined }}>
                            <button type="button" onClick={() => select(q.id)} aria-pressed={selected}
                              style={{ font: 'inherit', color: 'inherit', textAlign: 'left', background: 'transparent', border: 0, borderRadius: 16, padding: '16px 18px 10px', display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer' }}>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                                <Avatar text={q.builder.initials || q.builder.name} size={46} />
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                  <div className="mkt-row" style={{ gap: 8 }}>
                                    <strong style={{ fontSize: 16 }}>{q.builder.name}</strong>
                                    {q.status !== 'sent' && <span className={`mkt-pill ${qs.tone}`}>{qs.label}</span>}
                                    {q.version > 1 && <span className="mkt-pill">Version {q.version}</span>}
                                  </div>
                                  <div className="mkt-faint">{[q.builder.person !== q.builder.name ? q.builder.person : '', q.builder.headline].filter(Boolean).join(' · ') || 'Twasta builder'}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div className="mkt-faint">to build</div>
                                  <div style={{ fontSize: 19, fontWeight: 700 }}>{money(q.price_minor, q.currency)}</div>
                                  <div className="mkt-faint">then {monthlyShort(q.monthly, q.currency)}</div>
                                  <div className="mkt-faint">{weeksText(q.weeks)}</div>
                                </div>
                              </div>
                              {q.note && <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>{q.note.length > 220 ? `${q.note.slice(0, 217)}…` : q.note}</p>}
                              <div className="mkt-row" style={{ gap: 6 }}>
                                {total > 0 && <span className={`mkt-pill ${cov === total ? 'ok' : 'warn'}`}>Covers {cov} of {total} brief items</span>}
                                <span className="mkt-pill">{q.ai_mode === 'client' ? 'Uses your AI credits while building' : 'AI included'}</span>
                              </div>
                            </button>
                            <div className="mkt-row" style={{ gap: 6, padding: '0 18px 16px' }}>
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleShort(q)} aria-pressed={q.shortlisted} disabled={busy === `short:${q.id}`}>{q.shortlisted ? '★ Shortlisted' : '☆ Shortlist'}</button>
                              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, fontWeight: 600, padding: '0 8px', minHeight: 36, cursor: 'pointer' }}>
                                <input type="checkbox" checked={inCmp} disabled={!inCmp && cmp.length >= MAX_COMPARE} onChange={() => toggleCmp(q.id)} style={{ width: 18, height: 18, accentColor: 'var(--color-accent)' }} />
                                Compare
                              </label>
                              <span className="mkt-spacer" />
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => select(q.id)}>Details</button>
                            </div>
                          </div>
                        )
                      })}
                      {cmp.length >= MAX_COMPARE && <span className="mkt-faint">You can compare up to {MAX_COMPARE} estimates at a time.</span>}
                    </div>

                    {sel && (
                      <QuoteDetail
                        q={sel} req={req} items={items} busy={busy}
                        revising={revising} revisionText={revisionText} confirmDecline={confirmDecline}
                        onRevising={(on) => { setRevising(on); setConfirmDecline(false) }} onRevisionText={setRevisionText}
                        onSendRevision={() => sendRevision(sel)} onConfirmDecline={(on) => { setConfirmDecline(on); setRevising(false) }}
                        onDecline={() => doDecline(sel)} onShortlist={() => toggleShort(sel)}
                        onMessage={() => { if (sel.builder.user_id) { setThread(sel.builder.user_id); setTab('msg') } }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === 'msg' && (
              builders.length === 0 ? (
                <div className="mkt-empty">
                  <strong>No messages yet</strong>
                  <span className="mkt-muted">When a builder sends an estimate you can talk to them here.</span>
                </div>
              ) : (
                <div className="mkt-panel" style={{ padding: 0, display: 'flex', flexWrap: 'wrap', minHeight: 480, overflow: 'hidden' }}>
                  <div role="list" aria-label="Builders" style={{ flex: '1 1 260px', maxWidth: 340, borderRight: '1px solid var(--color-border)' }}>
                    {builders.map((b) => {
                      const on = thread === b.user_id
                      return (
                        <div role="listitem" key={b.user_id || b.tenant_id}>
                          <button type="button" aria-pressed={on} onClick={() => setThread(b.user_id)}
                            style={{ width: '100%', font: 'inherit', color: 'inherit', textAlign: 'left', border: 0, borderBottom: '1px solid var(--color-border)', padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', background: on ? 'var(--color-accent-weak)' : 'transparent' }}>
                            <Avatar text={b.initials || b.name} size={38} />
                            <span style={{ minWidth: 0 }}><strong style={{ fontSize: 14, display: 'block' }}>{b.name}</strong><span className="mkt-faint">{b.headline || b.person}</span></span>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    {(() => {
                      const b = builders.find((x) => x.user_id === thread)
                      const bq = quotes.find((q) => q.builder.user_id === thread)
                      return (
                        <>
                          <div className="mkt-row" style={{ padding: '14px 18px', borderBottom: '1px solid var(--color-border)', justifyContent: 'space-between' }}>
                            <strong>{b?.name || 'Choose a builder'}</strong>
                            {bq && <Link className="btn btn-secondary btn-sm" to={`/marketplace/build/quotes/${bq.id}`}>View their quote</Link>}
                          </div>
                          <div style={{ flex: '1 1 auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }} aria-live="polite">
                            {msgError ? (
                              <div className="mkt-banner bad" role="alert"><div><strong>Messages didn’t load</strong>{msgError}</div>{thread && <button type="button" className="btn btn-secondary btn-sm" onClick={() => loadThread(thread)}>Try again</button>}</div>
                            ) : messages === null ? (
                              <span className="mkt-muted" role="status">Loading messages…</span>
                            ) : messages.length === 0 ? (
                              <span className="mkt-muted">No messages yet. Ask {b?.name || 'the builder'} anything about their estimate.</span>
                            ) : messages.map((m) => {
                              if (m.kind !== 'message') {
                                return <div key={m.id} className="mkt-faint" style={{ alignSelf: 'center', textAlign: 'center' }}>{m.text} · {dateTime(m.at)}</div>
                              }
                              const mine = !!user && m.actor_user_id === user.user_id
                              return (
                                <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '78%', background: mine ? 'var(--color-accent)' : 'var(--color-raised, var(--color-bg))', color: mine ? '#fff' : 'var(--color-text)', borderRadius: 14, padding: '10px 14px', fontSize: 14, whiteSpace: 'pre-line' }}>
                                  {m.text}
                                  <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{m.actor || (mine ? 'You' : b?.name)} · {dateTime(m.at)}</div>
                                </div>
                              )
                            })}
                          </div>
                          <form className="mkt-row" style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', gap: 8, flexWrap: 'nowrap' }} onSubmit={(e) => { e.preventDefault(); send() }}>
                            <label htmlFor="rq-msg" className="mkt-sr">Message</label>
                            <input id="rq-msg" value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Write a message…" disabled={!thread}
                              style={{ flex: '1 1 auto', font: 'inherit', border: '1px solid var(--color-border-strong, var(--color-border))', borderRadius: 8, padding: '10px 12px', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
                            <button type="submit" className="btn btn-primary" disabled={sending || !msgText.trim() || !thread}>{sending ? 'Sending…' : 'Send'}</button>
                          </form>
                          <p className="mkt-faint" style={{ padding: '0 18px 12px', margin: 0 }}>Keep conversations and payments on Twasta — it is what protects you if something goes wrong.</p>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )
            )}

            {tab === 'brief' && <BriefView req={req} />}
          </>
        )}
      </div>
    </MarketplaceShell>
  )
}

function CompareTable({ list, items, cur, onClear }: { list: Quote[]; items: string[]; cur: string; onClear: () => void }) {
  const minOf = (vals: (number | null)[]) => { const v = vals.filter((x): x is number => x !== null && x > 0); return v.length ? Math.min(...v) : null }
  const minPrice = minOf(list.map((q) => q.price_minor))
  const minMonthly = minOf(list.map((q) => monthlyTotal(q.monthly)))
  const minYear = minOf(list.map((q) => firstYear(q)))
  const minWeeks = minOf(list.map((q) => q.weeks))
  const cov = (q: Quote) => q.covered ?? coveredCount(q, items)
  const maxCov = Math.max(...list.map(cov))
  const total = list[0]?.brief_total ?? items.length
  const rows: { label: string; cells: { v: string; best: boolean }[] }[] = [
    { label: 'To build', cells: list.map((q) => ({ v: money(q.price_minor, q.currency || cur), best: q.price_minor === minPrice })) },
    { label: 'To use, once installed', cells: list.map((q) => ({ v: monthlyShort(q.monthly, q.currency || cur), best: minMonthly !== null && monthlyTotal(q.monthly) === minMonthly })) },
    { label: 'First year in total', cells: list.map((q) => { const y = firstYear(q); return { v: y === null ? 'Depends on how many people use it' : money(y, q.currency || cur), best: y !== null && y === minYear } }) },
    { label: 'Time to go live', cells: list.map((q) => ({ v: weeksText(q.weeks), best: !!q.weeks && q.weeks === minWeeks })) },
    ...(total ? [{ label: 'Brief covered', cells: list.map((q) => ({ v: `${cov(q)} of ${total}`, best: cov(q) === maxCov })) }] : []),
    { label: 'AI while building', cells: list.map((q) => ({ v: q.ai_mode === 'client' ? 'Your AI credits' : 'Included', best: false })) },
    { label: 'Can start', cells: list.map((q) => ({ v: q.start_date ? fmtDate(q.start_date) : 'Not given', best: false })) },
    { label: 'Valid until', cells: list.map((q) => ({ v: q.valid_until ? fmtDate(q.valid_until) : '—', best: false })) },
  ]
  return (
    <div className="mkt-panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 17 }}>Side by side</strong>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClear}>Clear comparison</button>
      </div>
      <div className="mkt-scroll">
        <table className="mkt-table" style={{ minWidth: 640 }}>
          <thead><tr><th style={{ width: 190 }}><span className="mkt-sr">Compared on</span></th>{list.map((q) => <th key={q.id} scope="col">{q.builder.name}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <th scope="row" style={{ fontWeight: 600, color: 'var(--color-text)' }}>{r.label}</th>
                {r.cells.map((c, i) => (
                  <td key={i} style={c.best ? { color: 'var(--color-success)', fontWeight: 700 } : undefined}>
                    {c.v}{c.best && <span className="mkt-sr"> (best)</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="mkt-faint">The best value in each row is shown in green.</span>
    </div>
  )
}

function QuoteDetail(props: {
  q: Quote; req: BuildRequest; items: string[]; busy: string | null
  revising: boolean; revisionText: string; confirmDecline: boolean
  onRevising: (on: boolean) => void; onRevisionText: (t: string) => void; onSendRevision: () => void
  onConfirmDecline: (on: boolean) => void; onDecline: () => void; onShortlist: () => void; onMessage: () => void
}) {
  const { q, req, items, busy } = props
  const cur = q.currency
  const canOrder = !!req.approver && quoteLive(q)
  const canDecide = quoteLive(q)
  const year = firstYear(q)
  const adv = q.stages[0]
  const qs = quoteStatus(q)
  return (
    <aside className="mkt-panel" aria-label={`Estimate from ${q.builder.name}`} style={{ flex: '1 1 420px', maxWidth: 560, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <Avatar text={q.builder.initials || q.builder.name} size={56} />
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <strong style={{ fontSize: 19 }}>{q.builder.name}</strong>
          <div className="mkt-faint">{[q.builder.person !== q.builder.name ? q.builder.person : '', q.builder.headline].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={`mkt-pill ${qs.tone}`}>{qs.label}</span>
      </div>
      <Link to={`/marketplace/builders/${encodeURIComponent(q.builder.tenant_id)}`} style={{ fontSize: 14 }}>View their profile and past work →</Link>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, background: 'var(--color-raised, var(--color-bg))', borderRadius: 12, padding: 14 }}>
        <div><div className="mkt-faint">To build</div><strong style={{ fontSize: 18 }}>{money(q.price_minor, cur)}</strong></div>
        <div><div className="mkt-faint">Time</div><strong style={{ fontSize: 18 }}>{weeksText(q.weeks)}</strong></div>
        <div><div className="mkt-faint">Can start</div><strong style={{ fontSize: 18 }}>{q.start_date ? fmtDate(q.start_date) : 'Not given'}</strong></div>
      </div>

      {q.note && <div><strong>From {q.builder.name}</strong><p className="mkt-muted" style={{ fontSize: 14, margin: '4px 0 0', whiteSpace: 'pre-line' }}>{q.note}</p></div>}

      <div>
        <strong>Payment stages</strong>
        <p className="mkt-faint" style={{ margin: 0 }}>Twasta’s standard terms. You pay Twasta; Twasta pays the builder when you approve each stage.</p>
      </div>
      <PaymentStages stages={q.stages} rows={q.stages} amountMinor={q.price_minor} currency={cur} who="client" advanceRelease={q.terms?.advance_release} />

      {items.length > 0 && (
        <>
          <strong>Against your brief</strong>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px 14px' }}>
            {items.map((it) => {
              const c = covers(q, it)
              return (
                <li key={it} style={{ display: 'flex', gap: 8, fontSize: 14, alignItems: 'flex-start' }}>
                  <span aria-hidden="true" style={{ fontWeight: 700, color: c.included ? (c.note ? 'var(--color-warning)' : 'var(--color-success)') : 'var(--color-error)' }}>{c.included ? (c.note ? '!' : '✓') : '✕'}</span>
                  <span><span className="mkt-sr">{c.included ? 'Included: ' : 'Not included: '}</span>{it}{c.note && <span className="mkt-faint" style={{ display: 'block' }}>{c.note}</span>}</span>
                </li>
              )
            })}
          </ul>
        </>
      )}

      {req.questions.length > 0 && (
        <div className="mkt-col" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14, gap: 10 }}>
          <strong>Answers to your questions</strong>
          {req.questions.map((question, i) => (
            <div key={i}>
              <div className="mkt-faint" style={{ fontWeight: 600 }}>{question}</div>
              <p style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-line' }}>{q.answers[i] || <span className="mkt-muted">Not answered.</span>}</p>
            </div>
          ))}
        </div>
      )}

      {q.demos?.length > 0 && (
        <div className="mkt-col" style={{ gap: 6 }}>
          <strong>Similar work they can show you</strong>
          {q.demos.map((d) => (
            <div key={d.name} style={{ fontSize: 14 }}>
              {d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer">{d.name}</a> : <strong>{d.name}</strong>}
              {d.note && <span className="mkt-faint"> · {d.note}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="mkt-col" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14, gap: 8, fontSize: 14 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">AI used while building</span><strong>{q.ai_mode === 'client' ? 'Your AI credits' : 'Included in the price'}</strong></div>
        <div className="mkt-row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}><span className="mkt-muted">To use it, once installed</span><strong style={{ textAlign: 'right' }}>{monthlyText(q.monthly, cur)}</strong></div>
        <HostCallout currency={cur} />
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">First year in total</span><strong>{year === null ? 'Depends on how many people use it' : money(year, cur)}</strong></div>
        <div className="mkt-row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}><span className="mkt-muted">Where it’s built</span><strong style={{ textAlign: 'right' }}>In the builder’s account, then installed in yours</strong></div>
        {q.valid_until && <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Valid until</span><strong>{fmtDate(q.valid_until)}</strong></div>}
      </div>

      {q.status === 'revision_asked' && q.revision_note && (
        <div className="mkt-banner plain"><div><strong>You asked for a revision</strong><span style={{ whiteSpace: 'pre-line' }}>{q.revision_note}</span></div></div>
      )}

      <div className="mkt-row" style={{ gap: 10 }}>
        <Link className="btn btn-secondary" to={`/marketplace/build/quotes/${q.id}`} style={{ flex: '1 1 140px' }}>View the quote</Link>
        {canOrder && <Link className="btn btn-primary" to={`/marketplace/build/order/${q.id}`} style={{ flex: '1 1 200px' }}>Order from {q.builder.name}</Link>}
        <button type="button" className="btn btn-secondary" onClick={props.onMessage} style={{ flex: '1 1 140px' }} disabled={!q.builder.user_id}>Message</button>
      </div>
      {canDecide && !req.approver && <p className="mkt-faint" style={{ margin: 0 }}>Only your organization’s admins or project owners can place the order.</p>}

      <div className="mkt-row" style={{ gap: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={props.onShortlist} aria-pressed={q.shortlisted} disabled={busy === `short:${q.id}`}>{q.shortlisted ? '★ Shortlisted' : '☆ Shortlist'}</button>
        {canDecide && <button type="button" className="btn btn-ghost btn-sm" onClick={() => props.onRevising(!props.revising)} aria-expanded={props.revising}>Ask for a revision</button>}
        {canDecide && <button type="button" className="btn btn-ghost btn-sm" onClick={() => props.onConfirmDecline(true)}>Decline</button>}
      </div>

      {props.revising && (
        <div className="mkt-field">
          <label htmlFor="rq-rev">What should change?</label>
          <textarea id="rq-rev" rows={4} value={props.revisionText} maxLength={2000} onChange={(e) => props.onRevisionText(e.target.value)} placeholder="For example: could the second stage include the parts screen? Could you start a week earlier?" />
          <div className="mkt-row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={props.onSendRevision} disabled={busy === 'revision' || !props.revisionText.trim()}>{busy === 'revision' ? 'Sending…' : `Send to ${q.builder.name}`}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => props.onRevising(false)}>Cancel</button>
          </div>
        </div>
      )}
      {props.confirmDecline && (
        <div className="mkt-banner warn" role="alert">
          <div><strong>Decline this estimate?</strong>{q.builder.name} is told you won’t go ahead with it.</div>
          <div className="mkt-row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={props.onDecline} disabled={busy === 'decline'}>{busy === 'decline' ? 'Declining…' : 'Yes, decline'}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => props.onConfirmDecline(false)}>Keep it</button>
          </div>
        </div>
      )}

      {canOrder && adv && q.advance_due && (
        <p className="mkt-faint" style={{ margin: 0 }}>
          Placing the order takes the {adv.pct}% advance ({money(q.advance_due.total_minor, cur)} with Twasta’s service fee), paid to Twasta. Everything else is paid stage by stage, and Twasta pays the builder only after you approve each stage.
        </p>
      )}
    </aside>
  )
}

function BriefView({ req }: { req: BuildRequest }) {
  const d = req.details || {}
  const cur = req.currency
  const rows: [string, string][] = [
    ['Kind of work', req.kind_label],
    ['Must-haves', req.feature_labels.join(', ') || 'None'],
    ...(d.integration?.system ? [['Connects to', [d.integration.system, d.integration.version, d.integration.direction === 'in' ? 'into this app' : d.integration.direction === 'out' ? 'from this app' : 'both ways', d.integration.what].filter(Boolean).join(' · ')] as [string, string]] : []),
    ...(d.analytics?.db ? [['Data is in', [d.analytics.db, d.analytics.app, d.analytics.size, d.analytics.access].filter(Boolean).join(' · ')] as [string, string]] : []),
    ...(d.users ? [['People who will use it', d.users] as [string, string]] : []),
    ...(d.data ? [['Existing data to bring in', d.data] as [string, string]] : []),
    ['Budget', req.budget?.mode === 'range' ? `${money(req.budget.min_minor ?? 0, cur)} – ${money(req.budget.max_minor ?? 0, cur)}` : 'Builders estimate'],
    ['Timing', [req.timeline, req.start_date ? `start from ${fmtDate(req.start_date)}` : ''].filter(Boolean).join(' · ') || 'Not given'],
    ['May the builder also sell it', req.listing_allowed ? 'Yes, without our data' : 'No — only for us'],
  ]
  return (
    <div className="mkt-col" style={{ gap: 16 }}>
      {req.brief.length > 0 ? (
        <div className="mkt-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
          {req.brief.map((s) => (
            <div key={s.h}>
              <strong>{s.h} ({s.items.length})</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>{s.items.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
      ) : (
        <div className="mkt-empty"><strong>No itemised brief</strong><span className="mkt-muted">Builders priced the description and the details below.</span></div>
      )}
      <div className="mkt-panel">
        <table className="mkt-table">
          <tbody>
            {rows.map(([k, v]) => <tr key={k}><th scope="row" style={{ width: 240 }}>{k}</th><td>{v}</td></tr>)}
          </tbody>
        </table>
        {req.questions.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <strong>Questions every builder must answer</strong>
            <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 14 }}>{req.questions.map((q, i) => <li key={i}>{q}</li>)}</ol>
          </div>
        )}
      </div>
    </div>
  )
}
